import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import {
  CreateSubscriptionPlanRequest,
  UpdateSubscriptionPlanRequest,
  SubscriptionPlanResponse,
  SubscriptionPlanItemResponse
} from '../models';
import { environment } from 'src/app/environments/environment';

/**
 * Service for managing subscription packages with nested data structure
 * Uses SubscriptionPackages API endpoints
 * Packages contain nested items with charges
 */
@Injectable({
  providedIn: 'root'
})
export class SubscriptionPlanService {
  private readonly apiUrl = `${environment.companyApiUrl}/SubscriptionPackages`;

  constructor(private http: HttpClient) {}

  /**
   * Get all subscription packages (with nested items and charges)
   * @param appIds Optional comma-separated app IDs to filter packages
   */
  getAllPlans(appIds?: string): Observable<SubscriptionPlanResponse[]> {
    let params = new HttpParams();
    if (appIds) {
      params = params.set('AppIds', appIds);
    }

    return this.http.get<SubscriptionPlanResponse[]>(this.apiUrl, { params }).pipe(
      catchError(error => {
        console.error('Error loading subscription packages:', error);
        return of([]);
      })
    );
  }

  /**
   * Get plans with optional server-side search.
   */
  getPlans(searchTerm?: string): Observable<SubscriptionPlanResponse[]> {
    let params = new HttpParams();
    if (searchTerm && searchTerm.trim()) {
      params = params.set('SearchTerm', searchTerm.trim());
    }

    return this.http.get<SubscriptionPlanResponse[]>(this.apiUrl, { params }).pipe(
      catchError(error => {
        console.error('Error searching subscription packages:', error);
        return of([]);
      })
    );
  }

  /**
   * Get a specific package by ID (with nested items and charges)
   */
  getPlanById(id: number): Observable<SubscriptionPlanResponse | undefined> {
    return this.http.get<SubscriptionPlanResponse>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Error fetching plan:', error);
        return of(undefined);
      })
    );
  }



  /**
   * Create a new subscription package
   */
  createPlan(plan: CreateSubscriptionPlanRequest): Observable<SubscriptionPlanResponse> {
    return this.http.post<SubscriptionPlanResponse>(this.apiUrl, plan).pipe(
      catchError(error => {
        console.error('Error creating package:', error);
        throw error;
      })
    );
  }

  /**
   * Update an existing subscription package
   */
  updatePlan(id: number, updates: UpdateSubscriptionPlanRequest): Observable<SubscriptionPlanResponse | null> {
    return this.http.put<SubscriptionPlanResponse>(`${this.apiUrl}/${id}`, updates).pipe(
      catchError(error => {
        console.error('Error updating package:', error);
        throw error;
      })
    );
  }



  /**
   * Delete a subscription package
   */
  deletePlan(id: number): Observable<boolean> {
    return this.http.delete(`${this.apiUrl}/${id}`).pipe(
      map(() => true),
      catchError(error => {
        console.error('Error deleting package:', error);
        return of(false);
      })
    );
  }

  /**
   * Toggle package status (Active/Inactive)
   */
  togglePlanStatus(id: number, currentStatus: number): Observable<SubscriptionPlanResponse | null> {
    const newStatus = currentStatus === 2 ? 3 : 2;
    const statusRequest = { status: newStatus };

    return this.http.put<SubscriptionPlanResponse>(`${this.apiUrl}/${id}/changestatus`, statusRequest).pipe(
      catchError(error => {
        console.error('Error toggling package status:', error);
        return of(null);
      })
    );
  }
}
